import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum } from '@repo/toolkit/types'
import Button from '@repo/ui/button'
import UploadFileInput, { DisplayFiles } from '@repo/ui/file-input'
import { DownloadFile } from '@repo/ui/icons'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import useTranslate from '../../../../lib/hooks/use-translate'
import { CenterGroupDocumentInput } from '../../../../lib/types/models/center-group/center-group-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { FILE_VALIDATION } from '../../../../lib/utils/validations/file.validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<CenterGroupDocumentInput> & {
  mode: 'view' | 'edit'
  className?: string
  onCancel: () => void
}

const formSchema = z.object({
  documents: FILE_VALIDATION().array().default([]).nullable()
})

const UploadCenterGroupDocumentForm = ({
  mode,
  className,
  defaultValues,
  onCancel,
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()
  const form = useForm<CenterGroupDocumentInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { documents } = form.watch()

  if (mode === 'view') {
    return (
      <>
        <div className={cn('flex flex-col gap-4', className)}>
          <DisplayFiles files={documents} previewOnClick />
        </div>

        <div
          className={'flex justify-end w-full gap-3 pt-5 border-t border-t-white-active p-5 mt-5'}
        >
          <Button
            type={'button'}
            label={t('center.groups.close')}
            variant={'ghost'}
            onClick={onCancel}
          />
        </div>
      </>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <FormField
            control={form.control}
            name="documents"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <UploadFileInput
                    previewOnClick
                    types={[FileTypeEnum.IMAGE, FileTypeEnum.PDF]}
                    placeholder={t('center.groups.documents.placeholder')}
                    placeholderVariant="tag"
                    hasError={!!form.formState.errors.documents}
                    placeholderColor={tailwindColors.grey.lighter}
                    icon={DownloadFile}
                    multiple
                    label={t('center.groups.documents.upload')}
                    files={field?.value ? field?.value : undefined}
                    onFilesChange={(files) => {
                      field.onChange(files.length === 0 ? null : files)
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default UploadCenterGroupDocumentForm
